import React, { useRef } from 'react';
import { useImmer } from 'use-immer';
import { useSelector } from 'react-redux';
import {
  sendQuery,
  PAGE_OPTION_VALUE_QUERY,
  matrixSelector,
} from '../../../slices/matrixSlice';
import { cn } from '@/utils';
import {
  getCustomSizeType,
  getSingleValueShape,
  getRectangleShape,
  isSingleValueShape,
  isDiameter,
} from '../../../shapeUtils';
import PrimaryButton from "../../../../../components/molecules/Buttons";

const CustomFormat = ({
  parentId,
  shapes,
  values,
  onItemClick,
  custom_format,
  setMultipleOverwrites,
}) => {
  const { baseUrl, headers, productId, defaultConfig, isFetching, AveryCustom: { client } } =
    useSelector(matrixSelector);
  const { width, height } = custom_format;
  const {
    selection_custom_size_type,
    selection_id,
    values: shapeValues,
  } = shapes;
  const [componentState, updateComponentState] = useImmer({
    isValid: false,
    isFetching: false,
  });
  const widthInputRef = useRef(null);
  const heightInputRef = useRef(null);
  let reportValidityTimer = null;

  const btnAttributes = {
    type: 'button',
    large: 'true'
  }
  if (!componentState.isValid || componentState.isFetching || isFetching) {
    btnAttributes.disabled = true;
  }

  const handleInputChange = (e, otherRef) => {
    clearTimeout(reportValidityTimer);
    let valuesDifferentValid = true;
    e.target.setCustomValidity("");

    if (isDiameter(selection_id)) {
      // Diameter (round shape) uses a single value — sync both inputs.
      otherRef.current.value = e.target.value;
    } else if (e.target.value === otherRef.current.value && !isSingleValueShape(selection_id)) {
        // Equal values on a rectangle shape: only valid if a square shape exists to switch to.
        // Square shapes with equal values are always valid — no validation needed.
        valuesDifferentValid = getNewShape() !== undefined;
        if (!valuesDifferentValid) {
          e.target.setCustomValidity(Drupal.t("The width and height values must be different."));
        }
    }

    if (!e.target.checkValidity() && e.target.value.length > 0) {
      reportValidityTimer = setTimeout(() => {
          e.target.reportValidity();
      },500);
    }

    updateComponentState((draft) => {
      draft.isValid = e.target.checkValidity() && otherRef.current.checkValidity() && valuesDifferentValid;
    });
  };

  const getNewShape = () => {
    const possibleNewShape = getSingleValueShape(selection_id);
    return shapeValues.find(
      (ele) => ele.id === possibleNewShape
    );
  };

  const handleKeyDown = (e) => {
    if (
      (e.code === 'Enter' || e.code === 'NumpadEnter') &&
      componentState.isValid
    ) {
      sendCustomFormat();
    }
  };

  const handleSelectClick = () => {
    if (componentState.isValid) {
      sendCustomFormat();
    }
  };

  const sendCustomFormat = async () => {
    if (componentState.isValid) {
      const width = widthInputRef.current.value;
      const height = heightInputRef.current.value;
      const re = [
        new RegExp(`\_${width}x${height}mm`),
        new RegExp(`\_${width}mm`),
        new RegExp(`\^${width}\\s+x\\s+${height}\\s+mm$`), //<-- Regex for label
        new RegExp(`\_${width}(_\\d)*x${height}(_\\d)*mm`),
      ];

      updateComponentState((draft) => {
        draft.isFetching = true;
      });

      try {
        // Determine if we need to switch shape:
        // - Equal values on a rectangle → switch to square (e.g. sha_rectangle → sha_square)
        // - Different values on a square → switch back to rectangle (e.g. sha_square → sha_rectangle)
        // - Diameter shapes never switch — they only accept equal values via synced inputs.
        const needsShapeSwitch = width === height
          ? !isSingleValueShape(selection_id)
          : isSingleValueShape(selection_id) && !isDiameter(selection_id);

        const targetShape = needsShapeSwitch
          ? (width === height ? getSingleValueShape(selection_id) : getRectangleShape(selection_id))
          : false;
        const existingShape = targetShape
          ? shapeValues.find((ele) => ele.id === targetShape)
          : undefined;

        // Switch shape and query new format options for the target shape.
        // Falls through to else if target shape doesn't exist in this product.
        if (needsShapeSwitch && existingShape) {
          let { id: overwriteShape } = existingShape;
          let overwriteFormat = `custom_${width}_${height}_${getCustomSizeType(
            overwriteShape
          )}`;

          const response = await sendQuery({
            baseUrl,
            headers,
            query: PAGE_OPTION_VALUE_QUERY,
            variables: {
              productId,
              defaultConfig,
              optionsOverwrite: {
                shape: overwriteShape,
              },
              client
            },
          });

          if (response.data) {
            const {
              data: {
                webProduct: { page_options },
              },
            } = response;

            const format = page_options.find(({ id }) => id === 'format');
            if (format) {
              const existingSize = format.values.find(
                ({ id }) => re[0].test(id) || re[1].test(id)
              );
              if (existingSize) {
                const { id } = existingSize;
                overwriteFormat = id;
              }
            }
          }

          setMultipleOverwrites([
            { parentId: 'shape', id: overwriteShape },
            {
              parentId,
              id: overwriteFormat,
            },
          ]);
        } else {
          const existingSize = values.find(
              ({ id, label }) => re[0].test(id) || re[1].test(id) || re[2].test(label) || re[3].test(id)
          );
          if (existingSize) {
            const { id } = existingSize;
            onItemClick({
              parentId,
              id,
              is_selected: false,
            });
          } else {
            onItemClick({
              parentId,
              id: `custom_${width}_${height}_${selection_custom_size_type}`,
              is_selected: false,
            });
          }
        }
      } finally {
        updateComponentState((draft) => {
          draft.isFetching = false;
        });
      }
    }
  };

  return (
    <div
      className={cn(
        'px-2xl py-lg grid grid-cols-1 items-center justify-items-center gap-xs bg-neutral-medium font-medium text-sm',
        isDiameter(selection_id)
          ? 'tablet:grid-cols-3'
          : 'tablet:grid-cols-5',
        'tablet:gap-lg'
      )}
    >
      <div className="laptop:mb-lg">{Drupal.t('Enter individual size:')}</div>
      <div
        className={cn(
          !isDiameter(selection_id) ? 'tablet:col-span-3' : '',
          'flex w-full items-center'
        )}
      >
        <div className="flex w-full flex-col">
          <label className="relative input-label">
            <input
              id="custom-format-1"
              name="custom-format-1"
              aria-describedby="custom-format-1-descr"
              type="number"
              step="1"
              ref={widthInputRef}
              min={width.min}
              max={width.max}
              onChange={(e) => handleInputChange(e, heightInputRef)}
              onKeyDown={handleKeyDown}
              className="text-center"
              placeholder={
                isDiameter(selection_id)
                  ? Drupal.t('Diameter (mm)')
                  : Drupal.t('Width (mm)')
              }
              required
            />
            <span className="text">{
              isDiameter(selection_id)
                ? Drupal.t('Diameter (mm)')
                : Drupal.t('Width (mm)')
            }</span>
          </label>
          <span id="custom-format-1-descr" className="text-center text-xs mt-4xs">
            {width.min} - {width.max} mm
          </span>
        </div>
        <span
          className={cn(
            isDiameter(selection_id) ? 'hidden' : '',
            'px-lg mb-xs'
          )}
        >
          X
        </span>
        <div
          className={cn(
            isDiameter(selection_id) ? 'hidden' : '',
            'flex w-full flex-col'
          )}
        >
          <label className="relative input-label">
            <input
              id="custom-format-2"
              name="custom-format-2"
              aria-describedby="custom-format-2-descr"
              type="number"
              step="1"
              ref={heightInputRef}
              min={height.min}
              max={height.max}
              onChange={(e) => handleInputChange(e, widthInputRef)}
              onKeyDown={handleKeyDown}
              className="text-center"
              placeholder={Drupal.t('Height (mm)')}
              required
            />
            <span className="text">{Drupal.t('Height (mm)')}</span>
          </label>
          <span id="custom-format-2-descr" className="text-center text-xs mt-4xs">
            {height.min} - {height.max} mm
          </span>
        </div>
      </div>
        <div className="w-full tablet:pt-4xs tablet:self-start">
          <PrimaryButton
            {...btnAttributes}
            onClick={handleSelectClick}
        >
          {componentState.isFetching ? (
            <ft-spinner size="small"></ft-spinner>
          ) : (
            Drupal.t('Select')
          )}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default CustomFormat;
